/*
 * Purpose: Full-featured connector fix modal — replaces bare toasts for Gmail, secrets, MCP, etc.
 * Last modified: 2026-07-10 | Agent
 * Completeness: 100
 */
import { useMemo, useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { VaultUnlockInline } from '@/components/vault/VaultUnlockInline';
import { GmailConnectorFixPanel } from '@/components/connectors/GmailConnectorFixPanel';
import { SecretConnectorFixPanel } from '@/components/connectors/SecretConnectorFixPanel';
import { LinkedInConnectorPanel } from '@/components/LinkedInConnectorPanel';
import { McpConnectorPanel } from '@/components/McpConnectorPanel';
import { useConnectorFixStore } from '@/stores/connectorFixStore';
import type { ConnectorFixIssue } from '@/lib/connectorIssuePolicy';
import { useSecretStore } from '@/stores/secretStore';
import { AlertCircle, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';

function isGmailKind(kind: ConnectorFixIssue['kind']): boolean {
  return (
    kind === 'gmail_oauth' ||
    kind === 'gmail_inbox' ||
    kind === 'gmail_client_id' ||
    kind === 'gmail_no_messages'
  );
}

function ConnectorFixBody({
  issue,
  onFixed,
}: {
  issue: ConnectorFixIssue;
  onFixed: () => void;
}) {
  if (issue.kind === 'vault_unlock') {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">{issue.message}</p>
        <VaultUnlockInline onUnlocked={onFixed} />
      </div>
    );
  }

  if (isGmailKind(issue.kind)) {
    return <GmailConnectorFixPanel issue={issue} onFixed={onFixed} />;
  }

  if (issue.kind === 'openai_api_key' || issue.kind === 'generic_secret') {
    return <SecretConnectorFixPanel issue={issue} onFixed={onFixed} />;
  }

  if (issue.kind === 'linkedin') {
    return (
      <div className="-mx-1">
        <LinkedInConnectorPanel onConnected={onFixed} />
      </div>
    );
  }

  if (issue.kind === 'mcp') {
    return (
      <div className="-mx-1 max-h-[min(52vh,420px)] overflow-y-auto">
        <McpConnectorPanel compact onConnected={onFixed} />
      </div>
    );
  }

  return (
    <p className="text-sm text-muted-foreground">
      {issue.message} — use Connect setup or open the block config panel on the map.
    </p>
  );
}

export function ConnectorFixModal() {
  const open = useConnectorFixStore((s) => s.open);
  const session = useConnectorFixStore((s) => s.session);
  const closeConnectorFix = useConnectorFixStore((s) => s.closeConnectorFix);
  const isUnlocked = useSecretStore((s) => s.isUnlocked);

  const [refreshKey, setRefreshKey] = useState(0);
  const onFixed = () => setRefreshKey((t) => t + 1);

  const issues = session?.issues ?? [];
  const activeIssue = session?.issue;
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (open) setSelectedIndex(0);
  }, [open, session?.issue.kind, session?.workflowId]);

  const displayIssue = useMemo(() => {
    if (!activeIssue) return null;
    if (issues.length <= 1) return activeIssue;
    return issues[selectedIndex] ?? activeIssue;
    // refreshKey re-evaluates after inline fixes (vault unlock, OAuth, etc.)
  }, [activeIssue, issues, selectedIndex, refreshKey]);

  const handleRetry = async () => {
    const retry = session?.onRetry;
    closeConnectorFix();
    if (retry) await retry();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) closeConnectorFix();
      }}
    >
      <DialogContent className="max-w-[min(100vw-1.5rem,42rem)] max-h-[min(92dvh,720px)] overflow-y-auto p-4 sm:p-6">
        <DialogHeader className="space-y-2 pr-8">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-5 w-5 shrink-0 text-destructive mt-0.5" />
            <div className="min-w-0 flex-1">
              <DialogTitle className="text-base sm:text-lg leading-snug">
                {displayIssue?.title ?? 'Fix connection'}
              </DialogTitle>
              <DialogDescription className="text-xs sm:text-sm mt-1 text-left">
                {displayIssue?.message}
              </DialogDescription>
            </div>
          </div>

          {issues.length > 1 && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {issues.map((item, index) => (
                <button
                  key={`${item.kind}-${item.nodeId ?? index}`}
                  type="button"
                  onClick={() => setSelectedIndex(index)}
                  className={cn(
                    'rounded-full border px-2.5 py-0.5 text-[10px] font-medium transition-colors',
                    selectedIndex === index
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-border text-muted-foreground hover:bg-muted/50'
                  )}
                >
                  {item.title}
                </button>
              ))}
            </div>
          )}

          {!isUnlocked && displayIssue?.kind !== 'vault_unlock' && (
            <Badge variant="outline" className="w-fit text-[10px]">
              Vault locked — unlock below to save keys
            </Badge>
          )}
        </DialogHeader>

        {displayIssue && (
          <div className="py-1">
            <ConnectorFixBody issue={displayIssue} onFixed={onFixed} />
          </div>
        )}

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:gap-0 pt-2">
          <Button type="button" variant="ghost" size="sm" onClick={closeConnectorFix}>
            Close
          </Button>
          {session?.onRetry && (
            <Button type="button" size="sm" className="gap-1.5" onClick={() => void handleRetry()}>
              <RotateCcw className="h-3.5 w-3.5" />
              Retry run
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ConnectorFixModal;
